import styles from './StrengthsReport.module.scss';
import StudentRowSingleUnit from './StudentRowSingleUnit';
import StrengthsExpandable from './StrengthsExpandable';
import {
  Assignment,
  EmbeddedContent,
  Module,
  StrengthsEvaluationMetawork,
  MultipleChoiceWork,
} from '@peekapak/types/LessonPlanTypes';

interface StudentData {
  firstName: string;
  lastName?: string;
  userId: string;
  assignments: Assignment[];
}

interface DropdownOption {
  value: number;
  text: string;
  unitId: string;
}

interface StrengthsQuestions {
  index: number;
  question1: string;
  question2: string;
  strength1: string;
  strength2: string;
}

interface StrengthsExplanation {
  strength: string;
  description: string;
}

interface StudentWork extends Assignment {
  userId: string;
  createdAt: number;
  work: {
    content: [MultipleChoiceWork];
  };
  metawork: StrengthsEvaluationMetawork;
}

interface StrengthsEvaluation extends EmbeddedContent {
  questions: StrengthsQuestions[];
  tiebreakerQuestions: StrengthsQuestions[];
  content: StrengthsExplanation[];
  title: string;
}

interface Props {
  displayUnit: number;
  classData: StudentData[];
  moduleData: Module[];
  dropdownOptions: DropdownOption[];
  isShowStudents: boolean;
  isSmall: boolean;
}

const getDisplayQuestions = (moduleContent: StrengthsEvaluation, metawork: StrengthsEvaluationMetawork) => {
  const displayQuestions = metawork.questionOrder.reduce((acc, curr) => {
    const question = moduleContent.questions.find((item) => item.index === curr);
    if (question) {
      return [...acc, question];
    }
    return acc;
  }, [] as StrengthsQuestions[]);
  return displayQuestions;
};

const getTiebreakerQuestions = (moduleContent: StrengthsEvaluation, metawork: StrengthsEvaluationMetawork) => {
  const tiebreakerQuestions = metawork.tiebreakerOrder.reduce((acc, curr) => {
    const question = moduleContent.tiebreakerQuestions.find((item) => item.index === curr);
    if (question) {
      return [...acc, question];
    }
    return acc;
  }, [] as StrengthsQuestions[]);
  return tiebreakerQuestions;
};

const calculateScore = (assignment: StudentWork, moduleContent: StrengthsEvaluation) => {
  if (!assignment?.work?.content[0]?.content) return [];
  const answerArray = assignment.work.content[0].content as {
    index: number;
    answerIndex: number;
  }[];

  const combinedQuestions = [
    ...getDisplayQuestions(moduleContent, assignment.metawork),
    ...getTiebreakerQuestions(moduleContent, assignment.metawork),
  ];
  const strengthsObject = combinedQuestions.reduce(
    (acc: { [key: string]: number }, curr: StrengthsQuestions) => {
      const thisIndex = answerArray.find((item) => item.index === curr.index);
      if (!thisIndex || isNaN(thisIndex?.answerIndex)) return acc;
      if (thisIndex.answerIndex === 0)
        return acc?.[curr.strength1]
          ? { ...acc, [curr.strength1]: acc[curr.strength1] + 2 }
          : { ...acc, [curr.strength1]: 2 };
      if (thisIndex.answerIndex === 1)
        return acc?.[curr.strength1]
          ? { ...acc, [curr.strength1]: acc[curr.strength1] + 1 }
          : { ...acc, [curr.strength1]: 1 };
      if (thisIndex.answerIndex === 2)
        return acc?.[curr.strength2]
          ? { ...acc, [curr.strength2]: acc[curr.strength2] + 1 }
          : { ...acc, [curr.strength2]: 1 };
      if (thisIndex.answerIndex === 3)
        return acc?.[curr.strength2]
          ? { ...acc, [curr.strength2]: acc[curr.strength2] + 2 }
          : { ...acc, [curr.strength2]: 2 };
      return acc;
    },
    {} as { [key: string]: number },
  );
  const strengths = Object.keys(strengthsObject).filter((x) => {
    return strengthsObject[x] == Math.max.apply(null, Object.values(strengthsObject));
  });
  return strengths;
};

const incrementClassStrengths = (strength: string, classStrengths: { [key: string]: number }) => {
  const strengthsCount = Object.hasOwn(classStrengths, strength)
    ? {
        ...classStrengths,
        [strength]: classStrengths[strength] + 1,
      }
    : { ...classStrengths, [strength]: 1 };
  return strengthsCount;
};

const getTextColour = (moduleData: StrengthsEvaluation, strength: string) => {
  const index = moduleData.content.findIndex((item) => item.strength === strength);
  if (index === 0) return '#c6e4e2';
  if (index === 1) return '#fbf0b2';
  return '#e78d60';
};

const SingleUnit = ({
  classData,
  moduleData,
  isShowStudents,
  dropdownOptions,
  isSmall,
  displayUnit,
}: Props): JSX.Element => {
  const thisUnit = dropdownOptions.find((item) => item.value === displayUnit)!;

  const thisUnitId = thisUnit.unitId;

  const reviewContent =
    moduleData.find((item) => item.id.includes(thisUnitId)) &&
    moduleData.find((item) => item.id.includes(thisUnitId))!.studentContent[0];

  const moduleContent =
    moduleData.find((item) => item.id.includes(thisUnitId)) &&
    (moduleData
      .find((item) => item.id.includes(thisUnitId))!
      .studentContent[0].content.find((item) => item.type === 'strengthsEvaluation') as unknown as StrengthsEvaluation);

  let strengthsCount = {} as unknown as { [key: string]: number };
  const studentData =
    classData.length > 0 &&
    moduleData.length > 0 &&
    classData?.map((student) => {
      const assignment = student.assignments?.find((assignment) => assignment.unit.includes(thisUnitId)) ?? {
        module: thisUnitId,
        status: 'assigned',
      };
      const strength = calculateScore(assignment as StudentWork, moduleContent as StrengthsEvaluation);
      if (strength.length === 1) {
        const thisStrength = strength[0];
        strengthsCount = incrementClassStrengths(thisStrength, strengthsCount);
      }
      return {
        firstName: student.firstName,
        lastName: student.lastName,
        assignment: assignment,
        strength: strength,
        userId: student.userId,
      };
    });

  const totalComplete = Object.values(strengthsCount).reduce((acc: number, curr: number) => acc + curr, 0);

  return (
    <div className={styles.singleUnitLayout}>
      <div className={styles.singleUnitContainer}>
        <div className={styles.titleRow}>
          <div className={styles.nameCell}>
            <h4>{!isSmall ? 'Student Name' : 'Name'}</h4>
          </div>
          <div className={styles.unitTitles}>
            <div className={styles.titleCell}>
              <div className={styles.unitTitle}>
                {!isSmall && <h4>SELf Assessment</h4>}
                <p>Completion Status</p>
              </div>
            </div>
            <div className={styles.titleCell}>
              <div className={styles.unitTitle}>
                {!isSmall && <h4>Strength</h4>}
                <p>{dropdownOptions.find((item) => item.value === displayUnit)!.text}</p>
              </div>
            </div>
          </div>
        </div>
        {studentData &&
          studentData.map((student) => {
            const { firstName, lastName, assignment, strength, userId } = student;
            return (
              <StudentRowSingleUnit
                content={moduleContent as StrengthsEvaluation}
                reviewContent={reviewContent!}
                userId={userId}
                assignment={assignment}
                firstName={firstName}
                lastName={lastName ?? ''}
                isShowStudents={isShowStudents}
                strength={strength}
                key={`${userId}`}
                getTextColour={getTextColour}
              />
            );
          })}
      </div>
      <div className={styles.strengthsInfo}>
        {Object.keys(strengthsCount).length > 0 && (
          <div className={styles.calculationContainer}>
            <h3>Overall Classroom Strengths</h3>
            <p>For {dropdownOptions.find((item) => item.value === displayUnit)!.text}</p>
            {Object.entries(strengthsCount).map(([strength, count], index) => {
              return (
                <div
                  className={styles.strengthCard}
                  key={`${strength}${index}`}
                  style={{
                    backgroundColor: getTextColour(moduleContent as StrengthsEvaluation, strength),
                    color:
                      getTextColour(moduleContent as StrengthsEvaluation, strength.toString()) === '#e78d60'
                        ? 'white'
                        : 'inherit',
                  }}
                >
                  <div className={styles.strengthsPercentage}>{Math.floor((count / totalComplete) * 100)}%</div>
                  <p>{strength}</p>
                </div>
              );
            })}
          </div>
        )}
        <div className={styles.strengthsDescription}>
          {moduleContent &&
            moduleContent.content.map((strengthContent) => {
              const { strength, description } = strengthContent;
              return (
                <StrengthsExpandable
                  title={strength}
                  description={description}
                  key={`strength description${strength}`}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default SingleUnit;
